import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserState } from '../hooks/context/useUserState';
import { UPDATE_LAST_ACTIVITY } from '../constants';
import {
  hasActiveIdleHold,
  subscribeIdleHoldsCleared,
} from '../utils/idleHoldRegistry';
import { throttle } from 'lodash';

const eventArray = [
  'focus',
  'mousemove',
  'mousedown',
  'mousewheel',
  'keypress',
  'touchstart',
  'touchmove',
  'scroll',
];

const useIsMounted = () => {
  const ref = useRef(false);

  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);

  return () => ref.current;
};

const THROTTLE_TIME = 1000; // Adjust the throttle time as needed (e.g., 1000 milliseconds)

const useIdle = (timeToIdle: number): boolean => {
  const { dispatch } = useUserState();
  const [isIdle, setIsIdle] = useState(false);
  const isMounted = useIsMounted();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cleanTimeout = useCallback(() => {
    const timeoutId = timeoutIdRef.current;
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutIdRef.current = undefined;
    }
  }, []);

  const activityHandlerRef = useRef(
    throttle(() => {
      cleanTimeout();
      // Since the throttled function can be called when the component is no longer mounted,
      // we have to check that before updating component state
      const mounted = isMounted();
      if (!mounted || !navigator.onLine) return;
      // We should prevent from setting setLastActivity
      // when entering the page, as time already can pass
      dispatch({ type: UPDATE_LAST_ACTIVITY });

      setIsIdle(false);
      timeoutIdRef.current = setTimeout(() => {
        // A running export keeps the session alive; the countdown restarts once it ends
        if (!mounted || hasActiveIdleHold()) return;
        setIsIdle(true);
      }, timeToIdle);
    }, THROTTLE_TIME)
  );

  useEffect(() => {
    const activityHandler = activityHandlerRef.current;

    activityHandler();

    eventArray.forEach((eventName) => {
      document.addEventListener(eventName, activityHandler);
    });

    return () => {
      cleanTimeout();
      eventArray.forEach((eventName) => {
        document.removeEventListener(eventName, activityHandler);
      });
    };
  }, [cleanTimeout]);

  // Treats the end of a held operation as activity, which re-arms the full timeout
  useEffect(
    () => subscribeIdleHoldsCleared(() => activityHandlerRef.current()),
    []
  );

  return isIdle;
};

export default useIdle;
